/**
 * Topbar search display preference — persisted in localStorage, namespaced
 * per user so multiple accounts on the same browser do not leak each other's
 * preference (mirrors defaultCommunityView.ts / DEV-1019).
 *
 *   'bar'  → persistent full search bar centred in the topbar (default).
 *   'icon' → collapsed magnifier next to the notification bell; the search
 *            field animates open on click / ⌘K.
 *
 * Device-local by design (DEV-1389). The preference does NOT roam across
 * devices; a server-persisted version would require a user-settings column
 * + endpoint + migration and is intentionally out of scope here.
 */

import { useEffect, useState } from 'react';

export type SearchBarMode = 'bar' | 'icon';

const LS_KEY_BASE = 'l4e_search_bar_mode';

/** Dispatched on `window` after a write so the topbar reflects the new
 *  preference live, without a page reload. */
export const SEARCH_BAR_MODE_EVENT = 'l4e:search-bar-mode-changed';

function keyFor(userGuid?: string | null): string {
  const g = String(userGuid || '').trim().toUpperCase();
  return g ? `${LS_KEY_BASE}:${g}` : LS_KEY_BASE;
}

/** Read the saved search-bar mode for a user. Defaults to 'bar'. */
export function getSearchBarMode(userGuid?: string | null): SearchBarMode {
  try {
    if (window?.localStorage?.getItem(keyFor(userGuid)) === 'icon') return 'icon';
  } catch { /* SSR / privacy mode */ }
  return 'bar';
}

/** Persist the user's chosen search-bar mode (namespaced by user GUID) and
 *  notify live listeners. */
export function setSearchBarMode(value: SearchBarMode, userGuid?: string | null): void {
  try {
    window?.localStorage?.setItem(keyFor(userGuid), value);
    window?.dispatchEvent?.(new CustomEvent(SEARCH_BAR_MODE_EVENT));
  } catch { /* ignore */ }
}

/**
 * React hook: returns the current search-bar mode for a user and re-renders
 * when it changes (same tab via SEARCH_BAR_MODE_EVENT, other tabs via the
 * native 'storage' event).
 */
export function useSearchBarMode(userGuid?: string | null): SearchBarMode {
  const [mode, setMode] = useState<SearchBarMode>(() => getSearchBarMode(userGuid));
  useEffect(() => {
    const sync = () => setMode(getSearchBarMode(userGuid));
    sync(); // re-read if the user changed
    window.addEventListener(SEARCH_BAR_MODE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SEARCH_BAR_MODE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [userGuid]);
  return mode;
}
